import { FunctionMap, EntitiesState, Entities, Entity, CompetitionState } from '../../types';
import { DeleteEntity, FSAInterface } from '../actions';
import {
  SET_ENTITIES,
  REMOVE_FOLDER,
  ADD_FOLDER,
  SET_BOARD_HASHTAG_IMAGE,
  UPDATE_BOARD_SUCCESS,
  INCREMENT_PINNED_COUNT,
  DECREMENT_PINNED_COUNT,
  DELETE_BOARD_SUCCESS,
  CREATE_BOARD_SUCCESS,
  CREATE_COMPETITION_SUCCESS,
  DELETE_COMPETITION_SUCCESS,
  BOARD_BLOCK_USERNAME,
  BOARD_UNBLOCK_USERNAME,
  COMPETITION_BLOCK_USERNAME,
  COMPETITION_UNBLOCK_USERNAME,
  DUPLICATE_COMPETITION_SUCCESS,
  SAVE_COMPETITION_TYPE_SUCCESS,
  PUBLISH_BOARD_SUCCESS,
  EXPORT_COMPETITION_BOARD_SUCCESS,
} from '../constants';
import { createReducer } from './Utils';
import set from 'lodash/fp/set';
import unset from 'lodash/fp/unset';
import getOr from 'lodash/fp/getOr';
import uniq from 'lodash/fp/uniq';
import get from 'lodash/fp/get';

const entitiesDefaultState: EntitiesState = {
  entities: [],
};

function getCurrentEntity(currentEntityId: string, entities: Entities): Entity {
  return entities.find((b) => b._id === currentEntityId) || ({} as Entity);
}

function replaceEntity(newEntity: Entity, entities: Entities): Entities {
  const index = entities.findIndex((b: Entity) => b._id === newEntity._id);
  if (index !== -1) {
    return [...entities.slice(0, index), newEntity, ...entities.slice(index + 1)];
  }

  return entities;
}
function setEntities(state: EntitiesState, action: any) {
  return { ...state, entities: action.entities };
}

function addFolder(state: EntitiesState, action: any) {
  const entity = getCurrentEntity(action.currentBoardId, state.entities);
  const modifiedEntity = set(`folders.${action.folder._id}`, action.folder, entity || {});
  const entities = replaceEntity(modifiedEntity, state.entities);
  return { ...state, entities };
}

function removeFolder(state: EntitiesState, action: any) {
  const entity = getCurrentEntity(action.currentBoardId, state.entities);
  const mdofifiedEntity = unset(`folders.${action.folder._id}`, entity);
  const entities = replaceEntity(mdofifiedEntity, state.entities);
  return { ...state, entities };
}

function blockUsername(state: EntitiesState, action: any) {
  const entity = getCurrentEntity(action.currentEntityId, state.entities);

  const blockedSocialService: String[] = getOr([], `blocked.${action.socialService}`, entity);
  blockedSocialService.push(action.username);

  const modifiedEntity = set(`blocked.${action.socialService}`, uniq(blockedSocialService), entity);
  const entities = replaceEntity(modifiedEntity, state.entities);
  return { ...state, entities };
}

function unblockUsername(state: EntitiesState, action: any) {
  const entity = getCurrentEntity(action.currentEntityId, state.entities);
  const blockedSocialService: String[] = getOr([], `blocked.${action.socialService}`, entity);
  const without = blockedSocialService.filter((bu) => bu !== action.username);

  const modifiedEntity = set(`blocked.${action.socialService}`, without, entity);
  const entities = replaceEntity(modifiedEntity, state.entities);
  return { ...state, entities };
}

function setBoardHashtagImage(state: EntitiesState, action: any) {
  const board = getCurrentEntity(action.currentBoardId, state.entities);
  const tempBoard = set(`folders[${action.folderId}].settings.hashtagImage`, new Date().getTime(), { ...board });
  const newEntities = replaceEntity(tempBoard, state.entities);
  return { ...state, entities: [...newEntities] };
}

function updateEntity(state: EntitiesState, action: any) {
  const { payload } = action;
  const entities = state.entities.map((entity) => {
    if (entity._id !== payload._id) {
      return entity;
    }

    const formattedPayload = { ...payload };
    delete formattedPayload['members'];

    return { ...entity, ...formattedPayload };
  });

  return { ...state, entities };
}

function incrementPinnedCount(state: EntitiesState, action: any) {
  const { entityId } = action;
  const entities = state.entities.map((entity) => {
    if (entity._id !== entityId) {
      return entity;
    }

    return set('addedCount', entity.addedCount + 1, entity);
  });

  return { ...state, entities };
}

function decrementPinnedCount(state: EntitiesState, action: any) {
  const { entityId } = action;
  const entities = state.entities.map((entity) => {
    if (entity._id !== entityId) {
      return entity;
    }

    return set('addedCount', entity.addedCount - 1, entity);
  });

  return { ...state, entities };
}

function deleteEntity(state: EntitiesState, action: FSAInterface<DeleteEntity['payload']>) {
  const { entityId } = action.payload || {};
  const { entities } = state;
  const newEntities = entities.filter((entity: Entity) => entity._id !== entityId);
  return { ...state, entities: newEntities };
}

function addEntity(state: EntitiesState, action: any) {
  const entities = [...state.entities];
  const { payload: newEntity } = action;
  newEntity.addedCount = 0;
  entities.push(newEntity);
  return { ...state, entities: entities };
}

function insertEntityBeforeLast(state: EntitiesState, action: any) {
  const entities = [...state.entities];
  const { payload: newEntity } = action;
  newEntity.addedCount = 0;
  entities.splice(entities.length - 1, 0, newEntity);
  return { ...state, entities: entities };
}

function insertNewBoard(state: EntitiesState, action: any) {
  const entities = [...state.entities];
  const { payload: newEntity } = action;
  newEntity.addedCount = 0;
  const indexToAdd = state.entities.findIndex((entity) => entity.type === 'competition');
  entities.splice(indexToAdd, 0, newEntity);
  return { ...state, entities: entities };
}

function setCompetitionType(state: EntitiesState, action: any) {
  const { submissionType } = get('payload.metadata', action);
  const competitionId = get('payload._id', action);
  const selectedEntityIndex = state.entities.findIndex((entity) => entity._id === competitionId);
  const newEntities = [...state.entities];
  (newEntities[selectedEntityIndex] as CompetitionState).metadata.submissionType = submissionType;
  return { ...state, entities: newEntities };
}

const functionMap: FunctionMap = {
  [SET_ENTITIES]: setEntities,
  [REMOVE_FOLDER]: removeFolder,
  [ADD_FOLDER]: addFolder,
  [BOARD_BLOCK_USERNAME]: blockUsername,
  [BOARD_UNBLOCK_USERNAME]: unblockUsername,
  [COMPETITION_BLOCK_USERNAME]: blockUsername,
  [COMPETITION_UNBLOCK_USERNAME]: unblockUsername,
  [SET_BOARD_HASHTAG_IMAGE]: setBoardHashtagImage,
  [UPDATE_BOARD_SUCCESS]: updateEntity,
  [INCREMENT_PINNED_COUNT]: incrementPinnedCount,
  [DECREMENT_PINNED_COUNT]: decrementPinnedCount,
  [DELETE_BOARD_SUCCESS]: deleteEntity,
  [DELETE_COMPETITION_SUCCESS]: deleteEntity,
  [CREATE_BOARD_SUCCESS]: addEntity,
  [CREATE_COMPETITION_SUCCESS]: addEntity,
  [DUPLICATE_COMPETITION_SUCCESS]: insertEntityBeforeLast,
  [EXPORT_COMPETITION_BOARD_SUCCESS]: insertNewBoard,
  [SAVE_COMPETITION_TYPE_SUCCESS]: setCompetitionType,
  [PUBLISH_BOARD_SUCCESS]: updateEntity,
};

export default createReducer(functionMap, entitiesDefaultState);
